import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, X, Microphone, ThumbsUp, Heart, DotsThreeVertical, Trash, VideoCamera } from "@phosphor-icons/react";
import { api, fileUrl, avatarUrl, uploadFile } from "@/lib/api";
import { fileFromAudioBlob } from "@/lib/audioMime";
import { useAuth } from "@/lib/auth";
import { PHOTO_FILTER_CSS } from "@/lib/photoFilters";
import VoiceRecorder from "@/components/VoiceRecorder";
import Emoji from "@/components/Emoji";
import { toast } from "sonner";

export default function StoriesRing({ stories }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [viewing, setViewing] = useState(null);
  const [liveRooms, setLiveRooms] = useState([]);
  // Track which live room IDs we've already toasted about so we only fire a
  // single in-app notification when a follow goes live, not on every poll.
  const seenLiveIdsRef = React.useRef(new Set());

  // Poll for active LIVE broadcasts every 15s so the red ring appears
  // automatically when someone in the user's network goes live.
  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const { data } = await api.get("/rooms?kind=live");
        if (!alive) return;
        const next = Array.isArray(data) ? data : [];
        // Toast for any NEW live room (not in our seen set). Skip the very
        // first poll — those rooms were already live when we opened the
        // app and shouldn't bombard the user.
        if (seenLiveIdsRef.current.size > 0) {
          for (const r of next) {
            if (!seenLiveIdsRef.current.has(r.room_id) && r.host_id !== user?.user_id) {
              toast(`🔴 ${r.host_name} is live`, {
                description: r.topic || r.name,
                action: { label: "Watch", onClick: () => navigate(`/live/${r.room_id}`) },
              });
            }
          }
        }
        seenLiveIdsRef.current = new Set(next.map((r) => r.room_id));
        setLiveRooms(next);
      } catch { /* network blip — keep last list */ }
    };
    tick();
    const id = setInterval(tick, 15000);
    return () => { alive = false; clearInterval(id); };
  }, [navigate, user?.user_id]);

  const grouped = React.useMemo(() => {
    const map = new Map();
    (stories || []).forEach((s) => {
      if (!map.has(s.user_id)) map.set(s.user_id, []);
      map.get(s.user_id).push(s);
    });
    return Array.from(map.entries()).map(([uid, items]) => ({ uid, items, top: items[0] }));
  }, [stories]);

  return (
    <div className="px-4 py-4 border-b border-border/60" data-testid="stories-ring">
      <div className="flex gap-4 overflow-x-auto hide-scrollbar">
        <button
          type="button"
          onClick={() => navigate("/stories/create")}
          className="shrink-0 flex flex-col items-center gap-1.5 cursor-pointer group"
          data-testid="add-story"
        >
          <div className="relative h-[68px] w-[68px] rounded-full border-2 border-dashed border-white/20 p-[3px] group-hover:border-primary/50 transition">
            <img src={avatarUrl(user?.picture)} alt="" className="h-full w-full rounded-full object-cover opacity-90" />
            <span className="absolute -bottom-0.5 -right-0.5 h-6 w-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center border-2 border-background shadow-lg">
              <Plus size={14} weight="bold" />
            </span>
          </div>
          <span className="text-[11px] font-medium text-foreground/80 max-w-[64px] truncate">Your story</span>
        </button>

        {/* LIVE broadcasts — red pulsing ring, shown BEFORE regular stories so
            users notice when friends are live. Tapping opens the viewer. */}
        {liveRooms.map((lr) => (
          <button
            key={lr.room_id}
            type="button"
            onClick={() => navigate(`/live/${lr.room_id}`)}
            data-testid={`live-ring-${lr.room_id}`}
            className="shrink-0 flex flex-col items-center gap-1.5"
          >
            <div className="relative rounded-full p-[3px] bg-gradient-to-br from-red-500 via-pink-500 to-rose-600 shadow-[0_0_18px_rgba(244,63,94,0.55)]">
              <div className="rounded-full p-[2px] bg-background">
                <img src={avatarUrl(lr.host_picture)} alt="" className="h-[60px] w-[60px] rounded-full object-cover" />
              </div>
              <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 px-1.5 py-0.5 rounded-full bg-red-500 text-white text-[9px] font-bold uppercase tracking-widest flex items-center gap-0.5 shadow-lg">
                <span className="h-1 w-1 rounded-full bg-white animate-pulse" /> Live
              </span>
            </div>
            <span className="text-[11px] font-medium text-foreground/80 max-w-[68px] truncate">{lr.host_name}</span>
          </button>
        ))}

        {grouped.map((g) => (
          <button key={g.uid} onClick={() => setViewing(g)} data-testid={`story-${g.uid}`}
            className="shrink-0 flex flex-col items-center gap-1.5">
            <div className="rounded-full p-[3px] story-ring">
              <div className="rounded-full p-[2px] bg-background">
                <StoryThumbnail story={g.top} />
              </div>
            </div>
            <span className="text-[11px] font-medium text-foreground/80 max-w-[68px] truncate">{g.top.user_name}</span>
          </button>
        ))}
      </div>

      {viewing && <StoryViewer group={viewing} onClose={() => setViewing(null)} />}
    </div>
  );
}

/**
 * Tiny circular preview shown in the stories ring. We prefer the story's
 * actual media (a frame of the video, or the photo) so the ring functions
 * like a true content carousel rather than just a row of avatars. Falls back
 * to the author's avatar for legacy / voice-only stories without media.
 */
function StoryThumbnail({ story }) {
  const filterCss = PHOTO_FILTER_CSS[story.photo_filter || "none"] || "none";
  const mediaSrc = story.media_path ? fileUrl(story.media_path) : null;
  if (!mediaSrc) {
    return (
      <img
        src={avatarUrl(story.user_picture)}
        alt=""
        className="h-[60px] w-[60px] rounded-full object-cover"
      />
    );
  }
  if (story.media_type === "video") {
    return (
      <video
        src={mediaSrc}
        muted
        playsInline
        preload="metadata"
        className="h-[60px] w-[60px] rounded-full object-cover bg-black"
        style={{ filter: filterCss }}
        data-testid={`story-thumb-video-${story.story_id}`}
      />
    );
  }
  return (
    <img
      src={mediaSrc}
      alt=""
      loading="lazy"
      decoding="async"
      className="h-[60px] w-[60px] rounded-full object-cover bg-black"
      style={{ filter: filterCss }}
      data-testid={`story-thumb-photo-${story.story_id}`}
    />
  );
}

function StoryViewer({ group, onClose }) {
  const { user: me } = useAuth();
  const [idx, setIdx] = useState(0);
  const [showRecorder, setShowRecorder] = useState(false);
  const [sending, setSending] = useState(false);
  const [reacted, setReacted] = useState(null); // last reaction emoji for visual confirmation
  const [rain, setRain] = useState([]); // floating emoji bubbles for the publisher
  // Per-story playback duration in milliseconds. Defaults to 5s for photos
  // (matches Instagram/Facebook), but stretches to fit the actual length of a
  // video or voice clip. This was the root cause of the "story ends before it
  // finishes" bug — every story used to be capped at exactly 5s no matter what.
  const PHOTO_MS = 5000;
  const MAX_MS = 30000; // hard ceiling so a freakishly long voice file can't trap the viewer
  const [durationMs, setDurationMs] = useState(PHOTO_MS);
  const item = group.items[idx];
  const isMine = me && item.user_id === me.user_id;
  const paused = showRecorder || sending;
  const advanceTimerRef = useRef(null);
  const videoRef = useRef(null);
  const lastSeenReactionRef = useRef(new Set());

  // When the story index changes, reset duration to a sensible default before
  // the new media reports its real length. Voice clips have a known duration
  // from the backend; videos load asynchronously via `onLoadedMetadata`.
  useEffect(() => {
    if (!item) return;
    let next = PHOTO_MS;
    if (item.media_type === "video") {
      // Hold at a generous placeholder until <video> reports real duration —
      // we'd rather show a tiny bit too long than chop a story short.
      next = 15000;
    } else if (item.voice_path && typeof item.duration === "number" && item.duration > 0) {
      next = Math.min(MAX_MS, Math.max(PHOTO_MS, Math.round(item.duration * 1000)));
    }
    setDurationMs(next);
  }, [item]);

  // Pause/resume video playback in sync with the auto-advance timer. Without
  // this, opening the voice-reply recorder would keep the video playing
  // silently in the background — the user would dismiss the recorder only to
  // find the story already ended.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (paused) {
      try { v.pause(); } catch (_) { /* noop */ }
    } else {
      v.play().catch(() => { /* autoplay rejection handled by browser */ });
    }
  }, [paused, item?.story_id]);

  // Reaction rain — when the publisher views their OWN story, poll for fresh
  // reactions every 3 s and float each new emoji up from the bottom of the
  // screen (Facebook-Live-style). Each bubble auto-removes after 4 s.
  useEffect(() => {
    if (!isMine || !item?.story_id) return undefined;
    lastSeenReactionRef.current = new Set();
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/stories/${item.story_id}/reactions`);
        if (cancelled) return;
        const fresh = (data.reactions || []).filter((r) => r.emoji && !lastSeenReactionRef.current.has(r.reaction_id));
        if (fresh.length) {
          fresh.forEach((r) => lastSeenReactionRef.current.add(r.reaction_id));
          // Stagger so they don't all spawn on top of each other
          fresh.slice(0, 12).forEach((r, i) => {
            setTimeout(() => {
              const bubble = {
                id: r.reaction_id,
                emoji: r.emoji,
                x: 10 + Math.random() * 80, // 10–90% horizontal
                drift: (Math.random() * 60 - 30).toFixed(0), // -30 to +30 px sway
                delay: 0,
              };
              setRain((arr) => [...arr, bubble]);
              setTimeout(() => setRain((arr) => arr.filter((b) => b.id !== bubble.id)), 4200);
            }, i * 180);
          });
        }
      } catch (_) { /* silent — backend may be cold */ }
    };
    poll();
    const t = setInterval(poll, 3000);
    return () => { cancelled = true; clearInterval(t); };
  }, [isMine, item?.story_id]);

  // Auto-advance through stories — uses the real media duration (capped at
  // 30s) so videos finish playing and voice clips get heard in full. While
  // the user is actively reacting (recording / sending) we pause the timer
  // so they don't lose their place.
  useEffect(() => {
    if (paused) {
      if (advanceTimerRef.current) clearTimeout(advanceTimerRef.current);
      return undefined;
    }
    advanceTimerRef.current = setTimeout(() => {
      if (idx < group.items.length - 1) setIdx(idx + 1);
      else onClose();
    }, durationMs);
    return () => clearTimeout(advanceTimerRef.current);
  }, [idx, group.items.length, onClose, paused, durationMs]);

  const QUICK_CHIPS = [
    { id: "laugh", emoji: "😂", label: null },
    { id: "clap", emoji: "👏", label: null, render: () => (
      <span className="flex gap-0.5">
        <Emoji char="👏" size={22} />
        <Emoji char="👏" size={22} />
        <Emoji char="👏" size={22} />
      </span>
    )},
    { id: "fire", emoji: "🔥", label: null },
    { id: "wow", emoji: "😮", label: "Whoa!" },
    { id: "sad", emoji: "😢", label: null },
    { id: "love", emoji: "😍", label: null },
  ];

  const sendEmoji = async (emoji) => {
    if (sending || isMine) return;
    setSending(true);
    setReacted(emoji);
    try {
      await api.post(`/stories/${item.story_id}/reactions`, { emoji });
      // Subtle confirmation — Instagram does the same single toast.
      toast.success(`Sent ${emoji} to ${item.user_name}`);
    } catch (err) {
      setReacted(null);
      toast.error("Couldn't send reaction");
    } finally {
      setSending(false);
      // Clear the visual bump after a moment
      setTimeout(() => setReacted(null), 1500);
    }
  };

  const sendVoice = async ({ blob, duration }) => {
    if (sending || isMine) return;
    setSending(true);
    try {
      const file = fileFromAudioBlob(blob, "story-reply");
      const up = await uploadFile(file, "audio");
      await api.post(`/stories/${item.story_id}/reactions`, {
        voice_path: up.path,
        duration,
      });
      setShowRecorder(false);
      toast.success(`Voice reply sent to ${item.user_name}`);
    } catch (err) {
      toast.error("Couldn't send voice reply");
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async () => {
    if (!isMine || !item?.story_id) return;
    if (!window.confirm("Delete this story permanently? This can't be undone.")) return;
    try {
      await api.delete(`/stories/${item.story_id}`);
      toast.success("Story deleted");
      onClose();
      // FeedPage listens for this event and refetches /stories, so the rail
      // updates immediately without the user having to pull-to-refresh.
      window.dispatchEvent(new CustomEvent("voxly:refresh-stories"));
    } catch (err) {
      toast.error("Couldn't delete story");
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center" data-testid="story-viewer">
      <div className="absolute top-0 inset-x-0 p-3 flex gap-1">
        {group.items.map((it, i) => (
          <div key={it.story_id || `seg-${i}`} className="h-1 flex-1 rounded-full bg-white/20 overflow-hidden">
            <div
              className={`h-full bg-white ${i < idx ? "w-full" : i === idx ? "" : "w-0"}`}
              style={
                i === idx
                  ? {
                      // Drive the progress bar from the SAME durationMs as the
                      // auto-advance timer. Keeps the bar perfectly in sync
                      // with the actual playback time of each story.
                      width: "0%",
                      animation: `grow ${durationMs}ms linear forwards`,
                      animationPlayState: paused ? "paused" : "running",
                    }
                  : undefined
              }
            />
          </div>
        ))}
      </div>
      <div className="absolute top-3 right-3 z-20 flex items-center gap-2">
        {isMine && (
          <button
            type="button"
            onClick={handleDelete}
            data-testid="story-delete-btn"
            aria-label="Delete this story"
            className="h-10 w-10 rounded-full bg-destructive/85 hover:bg-destructive text-white flex items-center justify-center active:scale-90 transition shadow-lg"
          >
            <Trash size={18} weight="fill" />
          </button>
        )}
        <button
          onClick={onClose}
          className="h-10 w-10 rounded-full bg-white/10 backdrop-blur text-white flex items-center justify-center active:scale-90 transition"
          data-testid="story-close"
          aria-label="Close story"
        >
          <X size={20} />
        </button>
      </div>
      <div className="absolute top-6 left-4 flex items-center gap-3 z-10">
        <Link to={`/u/${item.user_id}`} onClick={onClose} className="flex items-center gap-3 hover:opacity-90">
          <img src={avatarUrl(item.user_picture)} className="h-9 w-9 rounded-full object-cover" alt="" />
          <span className="font-semibold text-sm text-white">{item.user_name}</span>
        </Link>
      </div>
      <div className="relative w-full h-full bg-black flex items-center justify-center">
        {(() => {
          const filterCss = PHOTO_FILTER_CSS[item.photo_filter || "none"] || "none";
          return item.media_type === "video" ? (
            <video
              key={item.story_id}
              ref={videoRef}
              src={fileUrl(item.media_path)}
              autoPlay
              playsInline
              className="max-w-full max-h-full w-auto h-auto object-contain"
              style={{ filter: filterCss }}
              data-testid={`story-video-${item.story_id}`}
              onLoadedMetadata={(e) => {
                // Sync the auto-advance timer + progress bar to the actual
                // video length (capped at 30s so a misuploaded long file
                // can't lock the viewer).
                const d = e.currentTarget?.duration;
                if (d && !isNaN(d) && d !== Infinity) {
                  setDurationMs(Math.min(MAX_MS, Math.max(PHOTO_MS, Math.round(d * 1000))));
                }
              }}
              onEnded={() => {
                // Natural end: advance immediately rather than waiting on the
                // timer (useful when the video is shorter than the fallback).
                if (idx < group.items.length - 1) setIdx(idx + 1);
                else onClose();
              }}
            />
          ) : item.media_path ? (
            <img src={fileUrl(item.media_path)} className="max-w-full max-h-full w-auto h-auto object-contain" alt="" style={{ filter: filterCss }} />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-primary/40 via-fuchsia-500/30 to-cyan-500/20 flex items-center justify-center">
              <p className="text-white/70 font-mono text-xs">VOICE-ONLY STORY</p>
            </div>
          );
        })()}

        {/* Emoji stickers — positioned relative to the viewport now, since the
            media is also viewport-sized. */}
        {(item.stickers || []).map((s, i) => (
          <span
            key={`${s.emoji}-${s.x ?? 0.5}-${s.y ?? 0.5}-${i}`}
            className="absolute select-none pointer-events-none"
            style={{
              left: `${(s.x ?? 0.5) * 100}%`,
              top: `${(s.y ?? 0.5) * 100}%`,
              transform: `translate(-50%,-50%) scale(${s.scale || 1})`,
              fontSize: 48,
              lineHeight: 1,
              filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.6))",
            }}
          >
            {s.emoji}
          </span>
        ))}

        {item.voice_path && (
          <audio
            src={fileUrl(item.voice_path)}
            autoPlay
            playsInline
            className="hidden"
            onLoadedMetadata={(e) => {
              // Defensive: if backend didn't persist a duration (legacy
              // stories created before that field was added), read it from
              // the loaded audio so the auto-advance timer still matches.
              const d = e.currentTarget?.duration;
              if (d && !isNaN(d) && d !== Infinity) {
                setDurationMs((prev) => {
                  const wanted = Math.min(MAX_MS, Math.max(PHOTO_MS, Math.round(d * 1000)));
                  // Only widen — never shorten, so a previously-set video
                  // duration on the same item isn't accidentally trimmed.
                  return Math.max(prev, wanted);
                });
              }
            }}
          />
        )}
        {item.music_url && (
          <>
            <audio src={item.music_url} autoPlay loop playsInline className="hidden" />
            <div className="absolute top-16 left-3 right-3 flex items-center gap-2 bg-black/60 backdrop-blur rounded-full px-3 py-1.5 pointer-events-none">
              <span className="text-xs text-white font-semibold truncate">🎵 {item.music_title || "Music"}</span>
            </div>
          </>
        )}
      </div>

      {/* Tap zones — must come BEFORE the reaction bar in DOM so the bar sits
          above them in the stacking context and receives taps cleanly. */}
      {!showRecorder && !isMine && (
        <>
          <button className="absolute left-0 top-0 w-1/3 z-[5]" style={{ bottom: 168 }} onClick={() => setIdx(Math.max(0, idx - 1))} aria-label="Previous" />
          <button className="absolute right-0 top-0 w-1/3 z-[5]" style={{ bottom: 168 }} onClick={() => idx < group.items.length - 1 ? setIdx(idx + 1) : onClose()} aria-label="Next" />
        </>
      )}
      {!showRecorder && isMine && (
        <>
          <button className="absolute left-0 inset-y-0 w-1/3 z-[5]" onClick={() => setIdx(Math.max(0, idx - 1))} aria-label="Previous" />
          <button className="absolute right-0 inset-y-0 w-1/3 z-[5]" onClick={() => idx < group.items.length - 1 ? setIdx(idx + 1) : onClose()} aria-label="Next" />
        </>
      )}

      {/* Facebook-style reaction toolbar — rendered as a sibling of the media
          container and the tap-zones so its z-index works deterministically. */}
      {!isMine && (
        <div
          className="absolute bottom-0 inset-x-0 z-30 px-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3 bg-gradient-to-t from-black/95 via-black/70 to-transparent"
          data-testid="story-reactions-bar"
          onClick={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
        >
          {/* Row 1 — quick-canned chips (Facebook's "😂 / 👏👏👏 / Whoa!" suggestions) */}
          <div className="flex items-center gap-2 mb-3 overflow-x-auto hide-scrollbar">
            {QUICK_CHIPS.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={(e) => { e.stopPropagation(); sendEmoji(c.emoji); }}
                disabled={sending}
                data-testid={`story-chip-${c.id}`}
                aria-label={`React ${c.label}`}
                className={`shrink-0 h-11 px-5 rounded-full flex items-center gap-1.5 bg-white/10 backdrop-blur-md border border-white/20 text-white font-bold active:scale-95 hover:bg-white/15 transition disabled:opacity-50 ${reacted === c.emoji ? "ring-2 ring-primary" : ""}`}
              >
                {c.render ? c.render() : <Emoji char={c.emoji} size={22} />}
                {c.label && <span className="text-sm">{c.label}</span>}
              </button>
            ))}
          </div>
          {/* Row 2 — Voice-message input pill + 3 big colored reactions (Facebook style) */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setShowRecorder(true); }}
              disabled={sending}
              data-testid="story-voice-reply-btn"
              className="flex-1 h-12 rounded-full bg-white/12 backdrop-blur-md border border-white/20 text-white/90 px-4 flex items-center gap-2 active:scale-[0.98] transition disabled:opacity-50"
            >
              <Microphone size={20} weight="fill" className="text-primary shrink-0" />
              <span className="text-sm font-semibold truncate">Voice reply to {item.user_name?.split(" ")[0] || "them"}…</span>
            </button>
            {/* Heart — red gradient */}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); sendEmoji("❤️"); }}
              disabled={sending}
              data-testid="story-react-heart"
              aria-label="React with heart"
              className={`shrink-0 h-12 w-12 rounded-full flex items-center justify-center text-white active:scale-90 hover:scale-110 transition disabled:opacity-50 shadow-lg ${reacted === "❤️" ? "ring-2 ring-white scale-110" : ""}`}
              style={{ background: "linear-gradient(135deg, #f43f5e 0%, #e11d48 100%)" }}
            >
              <Heart size={22} weight="fill" />
            </button>
            {/* Thumbs up — Facebook blue */}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); sendEmoji("👏"); }}
              disabled={sending}
              data-testid="story-react-thumbs"
              aria-label="React with thumbs up"
              className={`shrink-0 h-12 w-12 rounded-full flex items-center justify-center text-white active:scale-90 hover:scale-110 transition disabled:opacity-50 shadow-lg ${reacted === "👏" ? "ring-2 ring-white scale-110" : ""}`}
              style={{ background: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)" }}
            >
              <ThumbsUp size={22} weight="fill" />
            </button>
            {/* Laughing — yellow */}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); sendEmoji("😂"); }}
              disabled={sending}
              data-testid="story-react-laugh"
              aria-label="React with laughing"
              className={`shrink-0 h-12 w-12 rounded-full flex items-center justify-center active:scale-90 hover:scale-110 transition disabled:opacity-50 shadow-lg ${reacted === "😂" ? "ring-2 ring-white scale-110" : ""}`}
              style={{ background: "linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)" }}
            >
              <Emoji char="😂" size={26} />
            </button>
          </div>
        </div>
      )}
      <VoiceRecorder
        open={showRecorder}
        onClose={() => setShowRecorder(false)}
        onComplete={sendVoice}
        title="Voice reply"
        subtitle={`Replying to ${item.user_name}'s story`}
      />
      {/* Reaction rain overlay — only shown to the publisher viewing their own story. */}
      {isMine && rain.length > 0 && (
        <div className="pointer-events-none absolute inset-0 z-40 overflow-hidden" data-testid="story-reaction-rain">
          {rain.map((b) => (
            <span
              key={b.id}
              className="absolute bottom-0"
              style={{
                left: `${b.x}%`,
                "--drift": `${b.drift}px`,
                animation: "floatUp 4s ease-out forwards",
                fontSize: 44,
                lineHeight: 1,
                filter: "drop-shadow(0 4px 12px rgba(0,0,0,0.55))",
              }}
            >
              <Emoji char={b.emoji} size={44} />
            </span>
          ))}
        </div>
      )}
      <style>{`@keyframes grow { from { width: 0 } to { width: 100% } }`}</style>
    </div>
  );
}
